import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Settings as SettingsIcon, Globe, Volume2, Bell, Moon, Sun, Save } from 'lucide-react';

const SettingsContainer = styled.div`
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: #1a202c;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingsSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f8fafc;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionContent = styled.div`
  padding: 20px;
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const SettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Slider = styled.input`
  width: 100px;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
  }
`;

const Toggle = styled.button`
  width: 50px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => props.$active ? '#3b82f6' : '#e5e7eb'};
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$active ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
  }
`;

const ColorPicker = styled.input`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const SaveButton = styled(Button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
`;

const ResetButton = styled.button`
  padding: 8px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
    color: #374151;
  }
`;

function Settings() {
  const [settings, setSettings] = useState({
    language: 'ko',
    theme: 'light',
    ttsEnabled: true,
    ttsRate: 0.8,
    ttsPitch: 1.0,
    notifications: true,
    autoSave: true,
    dailyGoal: 20,
    accentColor: '#3b82f6',
    fontSize: 'medium'
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  };

  const saveSettings = () => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
    setHasChanges(false);
    alert('설정이 저장되었습니다.');
  };

  const resetSettings = () => {
    const defaultSettings = {
      language: 'ko',
      theme: 'light',
      ttsEnabled: true,
      ttsRate: 0.8,
      ttsPitch: 1.0,
      notifications: true,
      autoSave: true,
      dailyGoal: 20,
      accentColor: '#3b82f6',
      fontSize: 'medium'
    };
    setSettings(defaultSettings);
    setHasChanges(true);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const settingSections = [
    {
      title: '언어 설정',
      icon: Globe,
      settings: [
        {
          key: 'language',
          label: '기본 언어',
          description: '앱에서 사용할 기본 언어를 선택하세요.',
          type: 'select',
          options: [
            { value: 'ko', label: '한국어' },
            { value: 'en', label: 'English' },
            { value: 'ja', label: '日本語' }
          ]
        }
      ]
    },
    {
      title: '음성 설정',
      icon: Volume2,
      settings: [
        {
          key: 'ttsEnabled',
          label: 'TTS 활성화',
          description: '단어와 예문의 음성 재생을 활성화합니다.',
          type: 'toggle'
        },
        {
          key: 'ttsRate',
          label: '음성 속도',
          description: 'TTS 음성의 재생 속도를 조절합니다.',
          type: 'slider',
          min: 0.5,
          max: 2.0,
          step: 0.1
        },
        {
          key: 'ttsPitch',
          label: '음성 높이',
          description: 'TTS 음성의 높이를 조절합니다.',
          type: 'slider',
          min: 0.5,
          max: 2.0,
          step: 0.1
        }
      ]
    },
    {
      title: '알림 설정',
      icon: Bell,
      settings: [
        {
          key: 'notifications',
          label: '알림 활성화',
          description: '학습 목표 달성 등의 알림을 받습니다.',
          type: 'toggle'
        },
        {
          key: 'dailyGoal',
          label: '일일 목표',
          description: '하루에 학습할 단어 수를 설정합니다.',
          type: 'select',
          options: [
            { value: 10, label: '10단어' },
            { value: 20, label: '20단어' },
            { value: 30, label: '30단어' },
            { value: 50, label: '50단어' }
          ]
        }
      ]
    },
    {
      title: '화면 설정',
      icon: SettingsIcon,
      settings: [
        {
          key: 'theme',
          label: '테마',
          description: '앱의 색상 테마를 선택합니다.',
          type: 'select',
          options: [
            { value: 'light', label: '라이트 모드' },
            { value: 'dark', label: '다크 모드' }
          ]
        },
        {
          key: 'fontSize',
          label: '글자 크기',
          description: '앱의 글자 크기를 조절합니다.',
          type: 'select',
          options: [
            { value: 'small', label: '작게' },
            { value: 'medium', label: '보통' },
            { value: 'large', label: '크게' }
          ]
        },
        {
          key: 'accentColor',
          label: '강조 색상',
          description: '앱의 주요 색상을 선택합니다.',
          type: 'color'
        }
      ]
    },
    {
      title: '데이터 설정',
      icon: SettingsIcon,
      settings: [
        {
          key: 'autoSave',
          label: '자동 저장',
          description: '학습한 단어를 자동으로 저장합니다.',
          type: 'toggle'
        }
      ]
    }
  ];

  const renderSettingControl = (setting) => {
    switch (setting.type) {
      case 'select':
        return (
          <Select
            value={settings[setting.key]}
            onChange={(e) => updateSetting(setting.key, e.target.value)}
          >
            {setting.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        );
      
      case 'toggle':
        return (
          <Toggle
            $active={settings[setting.key]}
            onClick={() => updateSetting(setting.key, !settings[setting.key])}
          />
        );
      
      case 'slider':
        return (
          <Slider
            type="range"
            min={setting.min}
            max={setting.max}
            step={setting.step}
            value={settings[setting.key]}
            onChange={(e) => updateSetting(setting.key, parseFloat(e.target.value))}
          />
        );
      
      case 'color':
        return (
          <ColorPicker
            type="color"
            value={settings[setting.key]}
            onChange={(e) => updateSetting(setting.key, e.target.value)}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <SettingsContainer>
      <Title>
        <SettingsIcon size={32} />
        설정
      </Title>

      {settingSections.map((section, sectionIndex) => {
        const IconComponent = section.icon;
        return (
          <SettingsSection key={sectionIndex}>
            <SectionHeader>
              <SectionTitle>
                <IconComponent size={20} />
                {section.title}
              </SectionTitle>
            </SectionHeader>
            <SectionContent>
              {section.settings.map((setting, settingIndex) => (
                <SettingItem key={settingIndex}>
                  <SettingInfo>
                    <SettingLabel>{setting.label}</SettingLabel>
                    <SettingDescription>{setting.description}</SettingDescription>
                  </SettingInfo>
                  <SettingControl>
                    {renderSettingControl(setting)}
                    {setting.type === 'slider' && (
                      <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '30px' }}>
                        {settings[setting.key]}
                      </span>
                    )}
                  </SettingControl>
                </SettingItem>
              ))}
            </SectionContent>
          </SettingsSection>
        );
      })}

      <SettingsSection>
        <SectionHeader>
          <SectionTitle>데이터 관리</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <SettingItem>
            <SettingInfo>
              <SettingLabel>데이터 초기화</SettingLabel>
              <SettingDescription>
                모든 학습 데이터와 설정을 초기화합니다. 이 작업은 되돌릴 수 없습니다.
              </SettingDescription>
            </SettingInfo>
            <SettingControl>
              <ResetButton onClick={() => {
                if (window.confirm('정말로 모든 데이터를 초기화하시겠습니까?')) {
                  localStorage.clear();
                  window.location.reload();
                }
              }}>
                초기화
              </ResetButton>
            </SettingControl>
          </SettingItem>
        </SectionContent>
      </SettingsSection>

      {hasChanges && (
        <SaveButton onClick={saveSettings}>
          <Save size={16} />
          설정 저장
        </SaveButton>
      )}
    </SettingsContainer>
  );
}

export default Settings;
